import { EHeadingStyleVariant, Heading } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';

import ScrollMoreSvg from '@/assets/seasonal-guides/scroll-more.svg';
import MaxWidth from '@/components/common/layout/MaxWidth';

import css from './SeasonalGuideHero.module.css';
import { getMediaUrlForActiveConnection } from './utils';

type TSeasonalGuideHeroProps = {
  heroMediaMedBitrateUrl: string;
  heroMediaHighBitrateUrl: string;
  heroMediaFallbackUrl: string;
  heroMediaFallbackBlurImage: string;
  HeroBadgeAnimated: any;
  HeroBadgeStatic: any;
  header: string | React.ReactNode;
  description: string;
  footerText?: string;
  headerWidth?: number;
  scrollMoreArrow?: any;
};

export const SeasonalGuideHero = ({
  heroMediaMedBitrateUrl,
  heroMediaHighBitrateUrl,
  heroMediaFallbackUrl,
  heroMediaFallbackBlurImage,
  HeroBadgeAnimated,
  HeroBadgeStatic,
  header,
  description,
  footerText,
  headerWidth,
  scrollMoreArrow,
}: TSeasonalGuideHeroProps) => {
  const [heroMediaUrl, setHeroMediaUrl] = useState<string>();

  useEffect(
    () =>
      setHeroMediaUrl(
        getMediaUrlForActiveConnection(heroMediaMedBitrateUrl, heroMediaHighBitrateUrl),
      ),
    [heroMediaHighBitrateUrl, heroMediaMedBitrateUrl],
  );

  const fallbackMedia = (
    <Image
      src={heroMediaFallbackUrl}
      alt=""
      layout="fill"
      objectFit="cover"
      objectPosition="center"
      placeholder="blur"
      blurDataURL={heroMediaFallbackBlurImage}
      priority={true}
      quality={75}
    />
  );

  return (
    <div
      className={cn(
        'relative text-white h-[calc(100vh-72px)] min-h-[600px] md:min-h-[800px]',
        css.root,
      )}>
      {heroMediaUrl && (
        <div className="absolute inset-0 z-10">
          <video autoPlay playsInline loop muted className="object-cover w-full h-full">
            <source src={heroMediaUrl} type="video/mp4" />
            {fallbackMedia}
          </video>
        </div>
      )}

      <div className="absolute inset-0 z-20">
        <MaxWidth className="justify-center h-full mx-auto gap-y-6">
          <div className="order-2 mx-auto lg:order-1">
            <div className="lg:absolute lg:right-4 xxxl:right-16 lg:top-4 lg:mt-20">
              <div className="relative w-24 h-24 md:h-40 md:w-40 lg:w-[200px] lg:h-[200px]">
                <HeroBadgeAnimated className="absolute w-full h-full animate-[spin_10s_linear_infinite]" />
                <HeroBadgeStatic className="absolute w-full h-full" />
              </div>
            </div>
          </div>

          <div className="order-1 mx-auto mt-20 lg:h-full lg:order-2 lg:mt-0">
            <div className="flex flex-col items-center px-1 lg:px-6 lg:justify-center lg:h-full gap-y-6">
              <div
                className={cn('max-w-[341px] md:max-w-[720px]', css.title, css.parallaxSVGs)}
                style={{ width: headerWidth }}>
                <Heading
                  variant={EHeadingStyleVariant.H1}
                  className="items-center font-semibold text-center font-[venice-blvd]">
                  {header}
                </Heading>
              </div>

              <h5 className={cn('text-center', css.description, css.parallaxText)}>
                {description}
              </h5>
            </div>
          </div>
        </MaxWidth>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-30">
        <div className="hidden lg:flex lg:flex-col lg:justify-center lg:py-6">
          <div className={cn('flex justify-center uppercase', css.more)}>
            {footerText || 'Leaf through the guide'}
          </div>
          <div className="flex justify-center mt-4">{scrollMoreArrow || <ScrollMoreSvg />}</div>
        </div>
      </div>
    </div>
  );
};
